import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "../componentLibrary/Modal";
import Spinner from "../componentLibrary/Spinner";
import Button from "../componentLibrary/Button";
import {
  invalidateLessonFeedbackQueries,
  useGetLessonFeedback,
} from "../api/queries";
import { useResolveFeedback } from "../api/mutations";
import { usePermissions } from "../contexts/PermissionContext";
import { FeedbackValue } from "../types";
import FeedbackCard from "./FeedbackCard";

interface LessonFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: string;
  lessonTitle?: string;
}

const LessonFeedbackModal: React.FC<LessonFeedbackModalProps> = ({
  isOpen,
  onClose,
  lessonId,
  lessonTitle,
}) => {
  const [showResolved, setShowResolved] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { hasAllPermissions } = usePermissions();
  const canResolve = hasAllPermissions(["content:update", "analytics:update"]);

  const { data: feedback, isLoading, isError } = useGetLessonFeedback(lessonId);

  useEffect(() => {
    if (isOpen && lessonId) {
      invalidateLessonFeedbackQueries(queryClient, lessonId);
    }
  }, [isOpen, lessonId, queryClient]);

  const resolveMutation = useResolveFeedback({
    onSuccess: async () => {
      invalidateLessonFeedbackQueries(queryClient, lessonId);
      setResolvingId(null);
    },
    onError: () => {
      setResolvingId(null);
    },
  });

  const handleResolveToggle = (feedbackId: string, isResolved: boolean) => {
    setResolvingId(feedbackId);
    resolveMutation.mutate({ feedbackId, isResolved });
  };

  // Calculate statistics (based on all feedback regardless of filter)
  const stats = useMemo(() => {
    if (!feedback || feedback.length === 0) {
      return {
        total: 0,
        byRating: {
          very_useful: 0,
          moderately_useful: 0,
          not_at_all_useful: 0,
        },
        averageRating: null,
      };
    }

    const ratingCounts = {
      very_useful: 0,
      moderately_useful: 0,
      not_at_all_useful: 0,
    };

    let totalNumericRating = 0;
    let ratingsWithValue = 0;

    feedback.forEach((item) => {
      if (item.feedback) {
        const feedbackValue = item.feedback as FeedbackValue | string;
        if (
          feedbackValue === FeedbackValue.VERY_USEFUL ||
          feedbackValue === "very_useful"
        ) {
          ratingCounts.very_useful++;
          totalNumericRating += 5;
          ratingsWithValue++;
        } else if (
          feedbackValue === FeedbackValue.MODERATELY_USEFUL ||
          feedbackValue === "moderately_useful"
        ) {
          ratingCounts.moderately_useful++;
          totalNumericRating += 3;
          ratingsWithValue++;
        } else if (
          feedbackValue === FeedbackValue.NOT_AT_ALL_USEFUL ||
          feedbackValue === "not_at_all_useful"
        ) {
          ratingCounts.not_at_all_useful++;
          totalNumericRating += 1;
          ratingsWithValue++;
        }
      }
    });

    const averageRating =
      ratingsWithValue > 0 ? totalNumericRating / ratingsWithValue : null;

    return {
      total: feedback.length,
      byRating: ratingCounts,
      averageRating,
    };
  }, [feedback]);

  const resolvedCount = useMemo(
    () => (feedback ? feedback.filter((f) => f.isResolved === true).length : 0),
    [feedback],
  );

  const displayedFeedback = useMemo(
    () =>
      feedback
        ? feedback.filter((item) =>
            showResolved ? item.isResolved === true : item.isResolved !== true,
          )
        : [],
    [feedback, showResolved],
  );

  return (
    <Modal
      title={`Feedback for ${lessonTitle || "Lesson"}`}
      subtitle="Viewing all feedback submitted for this lesson"
      isOpen={isOpen}
      onCloseModal={onClose}
      size="lg"
      height="80vh"
      maxHeight="90vh"
    >
      <div className="space-y-4">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner className="mx-auto" />
          </div>
        )}

        {isError && (
          <div className="text-center text-red-600 p-4">
            Error loading feedback. Please try again.
          </div>
        )}

        {!isLoading && !isError && feedback && feedback.length === 0 && (
          <div className="text-center text-gray-500 p-8">
            <p className="text-lg font-semibold mb-2">No feedback yet</p>
            <p className="text-sm">
              No users have submitted feedback for this lesson yet.
            </p>
          </div>
        )}

        {!isLoading && !isError && feedback && feedback.length > 0 && (
          <>
            {/* Statistics Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-electrum-50 border border-electrum-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Total Feedback Entries
                </p>
                <p className="text-3xl font-bold text-electrum-700">
                  {stats.total}
                </p>
              </div>

              {stats.averageRating !== null && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Average Rating
                  </p>
                  <p className="text-3xl font-bold text-blue-700">
                    {stats.averageRating.toFixed(2)} / 5
                  </p>
                </div>
              )}
            </div>

            {/* Rating Distribution */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Rating Distribution
              </p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Very Useful</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{
                          width: `${stats.total > 0 ? (stats.byRating.very_useful / stats.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-8 text-right">
                      {stats.byRating.very_useful}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Useful</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-yellow-500 h-2 rounded-full"
                        style={{
                          width: `${stats.total > 0 ? (stats.byRating.moderately_useful / stats.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-8 text-right">
                      {stats.byRating.moderately_useful}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Not that useful</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-red-500 h-2 rounded-full"
                        style={{
                          width: `${stats.total > 0 ? (stats.byRating.not_at_all_useful / stats.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-8 text-right">
                      {stats.byRating.not_at_all_useful}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feedback Cards Header with Resolved Toggle */}
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">
                {showResolved
                  ? `Resolved Feedback (${resolvedCount})`
                  : `Unresolved Feedback (${stats.total - resolvedCount})`}
              </p>
              {canResolve && (
                <Button
                  variant={showResolved ? "primary" : "secondary"}
                  onClick={() => setShowResolved((prev) => !prev)}
                  className="text-sm"
                >
                  {showResolved
                    ? `Hide Resolved (${resolvedCount})`
                    : `Show Resolved (${resolvedCount})`}
                </Button>
              )}
            </div>

            {displayedFeedback.length === 0 ? (
              <div className="text-center text-gray-500 p-6">
                <p className="text-sm">
                  {showResolved
                    ? "No resolved feedback entries."
                    : "No unresolved feedback entries."}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {displayedFeedback.map((item) => (
                  <FeedbackCard
                    key={item._id}
                    feedback={item}
                    canResolve={canResolve}
                    onResolveToggle={handleResolveToggle}
                    isResolving={resolvingId === item._id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default LessonFeedbackModal;
